import { Check, X, Shield, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { perDayPrice, billedText, annualSaving, annualFreeMonths, formatEuro } from '../lib/pricingPlans';

const THEMES = {
  founder: {
    card: 'bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] text-white shadow-2xl shadow-purple-500/30',
    badge: 'bg-gradient-to-r from-[#F59E0B] to-[#EF4444]',
    button: 'bg-white text-[#8B5CF6] hover:bg-white/90'
  },
  standard: {
    card: 'bg-gradient-to-br from-[#2F6B45] to-[#4A7C59] text-white shadow-2xl shadow-green-700/25',
    badge: 'bg-gradient-to-r from-[#F59E0B] to-[#F97316]',
    button: 'bg-white text-[#2F6B45] hover:bg-white/90'
  },
  premium: {
    card: 'bg-gradient-to-br from-[#1E3A5F] to-[#2C5282] text-white shadow-2xl shadow-blue-900/25',
    badge: 'bg-gradient-to-r from-[#3B82F6] to-[#06B6D4]',
    button: 'bg-white text-[#1E3A5F] hover:bg-white/90'
  }
};

const FeatureList = ({ features, planId }) => (
  <ul className="space-y-2.5 mb-6 flex-1">
    {features.map((feature) => (
      <li
        key={feature.text}
        className={`flex items-start gap-2 text-sm ${feature.included ? 'text-white' : 'text-white/40'}`}
        data-testid={`plan-${planId}-feature-${feature.included ? 'included' : 'excluded'}`}
      >
        {feature.included
          ? <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
          : <X className="w-4 h-4 mt-0.5 flex-shrink-0" />}
        <span>{feature.text}</span>
      </li>
    ))}
  </ul>
);

const PaymentMethods = () => (
  <div className="text-xs mb-4 p-2 rounded-lg bg-white/10 text-white/70">
    💳 Carta, Apple Pay, Google Pay
    <br />
    🏦 Bonifico Bancario
  </div>
);

const PlanCard = ({ plan, period, onSelect, loadingPlanId, currentPlanId, showPaymentMethods }) => {
  const theme = THEMES[plan.theme];
  const amount = plan.prices[period];
  const isCurrent = currentPlanId === plan.id;
  const saving = period === 'annual' ? annualSaving(plan) : 0;
  const label = plan.soldOut ? 'Posti esauriti' : isCurrent ? 'Piano Attuale' : plan.cta;

  return (
    <div className={`relative rounded-3xl p-6 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${theme.card}`} data-testid={`plan-card-${plan.id}`}>
      {plan.badge && (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-bold px-4 py-1.5 rounded-full shadow-lg text-white ${plan.soldOut ? 'bg-gray-500' : theme.badge}`}
          data-testid={`plan-badge-${plan.id}`}
        >
          {plan.badge}
        </div>
      )}
      <h3 className="text-xl font-bold mt-2">{plan.name}</h3>
      <p className="text-sm text-white/75 mb-5">{plan.subtitle}</p>

      <div className="mb-1" data-testid={`plan-${plan.id}-per-day`}>
        <span className="text-5xl font-extrabold tracking-tight">{perDayPrice(amount, period)}</span>
        <span className="text-base text-white/80 ml-1">/giorno</span>
      </div>
      <p className="text-xs text-white/70 mb-2" data-testid={`plan-${plan.id}-billed`}>{billedText(amount, period)}</p>
      {saving > 0 && (
        <p className="inline-block self-start text-xs font-semibold bg-white/15 rounded-full px-3 py-1 mb-2" data-testid={`plan-${plan.id}-annual-saving`}>
          {annualFreeMonths(plan)} mesi gratis · Risparmi {formatEuro(saving)}
        </p>
      )}
      <div className="h-px bg-white/15 my-4" />

      <FeatureList features={plan.features} planId={plan.id} />
      {showPaymentMethods && period === 'annual' && <PaymentMethods />}

      <div className="mt-auto">
        <Button
          onClick={() => onSelect(plan)}
          disabled={plan.soldOut || isCurrent || loadingPlanId === plan.id}
          className={`w-full py-6 rounded-xl font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${theme.button}`}
          data-testid={`select-${plan.id}-plan`}
        >
          {loadingPlanId === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : label}
          {!plan.soldOut && !isCurrent && loadingPlanId !== plan.id && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
        <p className="mt-3 text-center text-[11px] leading-tight text-white/70">
          <Shield className="w-3 h-3 inline mr-1" />
          Rimborso garantito entro 14 giorni
        </p>
      </div>
    </div>
  );
};

export const PlanCards = ({ plans, period, onSelect, loadingPlanId = null, currentPlanId = null, showPaymentMethods = false }) => (
  <div className={`grid gap-6 items-stretch pt-3 ${plans.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 max-w-4xl mx-auto'}`}>
    {plans.map((plan) => (
      <PlanCard
        key={plan.id}
        plan={plan}
        period={period}
        onSelect={onSelect}
        loadingPlanId={loadingPlanId}
        currentPlanId={currentPlanId}
        showPaymentMethods={showPaymentMethods}
      />
    ))}
  </div>
);
